/***
 *
 * Component for Medical Reports
 * @description
 * @author Sameena
 */

import { useEffect, useState, useRef, useMemo, useContext } from 'react';
import VisitInfo from '../../../emr/components/visit-info/visit-info';
import { Form, Row, Col, Modal, Select, Input, Typography, Skeleton, message, Empty, Checkbox, Tabs } from 'antd';
import { ReferenceSelect, useTranslation, Card, Button, GlobalContext } from 'soxo-bootstrap-core';
import AiNotes from '../../../screening/generate-ai-notes/generate-ai-notes';
import { TemplateMasters, ModalityGroups } from '../../../../models';
import './medical-reports.scss';
import { Opreg, MedicalReports as MedicalReport } from '../../../../models';
import ReportList from '../report-list/report-list';
import CustomAlert from '../../../../components/alert';
import extractOtherDetails from '../../../../utils/extract-other-details';
import PrintPreview from '../print-preview/print-preview';
import ReportEditor from './report-editor';

const { Option } = Select;
const { TextArea } = Input;

const formItemLayout = {
    labelCol: {
        // xs: { span:0 },
        // sm: { span: 0},
    },

    wrapperCol: {
        span: 50,
    },

    layout: 'vertical',
};

export default function MedicalReports({
    billId,
    opNumber,
    /**manage ai report modal */
    // generateAiReportModal,
    reference_number,
    /**Template code from attribute */
    templateCode,
    /**Guets data to be set for guest info */
    guestData,
    isShowVisitInfo = true,
    // Disable Search All Branches checkbox
    disableCheckBox,
    groupPointer,
    aiReportEditDisable, // Disable AI Report edit for follow up screen
    ...props
}) {
    const generateAiReportModal = false;

    const [opNo, setOpNo] = useState(opNumber ? opNumber : null);

    const [guestDetails, setGuestDetails] = useState('');

    const [btnloading, setBtnloading] = useState(false);

    const [initial, setInitial] = useState({ report_status: 'D' });

    const [currentReportId, setCurrentReportId] = useState(null);

    const [loading, setLoading] = useState(false);

    const [template, setTemplate] = useState();
    // state to manage cancel modal visibility
    const [cancelReportModalVisible, setCancelReportModalVisible] = useState(false);
    /**Sett referenceId as billid directly if it is to manage ai report */
    const [referenceId, setReferenceId] = useState(billId ? billId : null);

    const [html, setHtml] = useState(null);

    const [templateDetails, setTemplateDetails] = useState({});
    // used to save templatemas details
    const [options, setOptions] = useState([]);

    const [templateLoading, setTemplateLoading] = useState(false);
    // store api result
    const [medicalReport, setMedicalReport] = useState({});

    const [mode, setMode] = useState();

    const { Title, Text } = Typography;

    const [form] = Form.useForm();

    const { t, i18n } = useTranslation(); // To Translate to another language

    const [checked, setChecked] = useState(false);

    const [activeTabKey, setActiveTabKey] = useState('1');

    const [checkedmail, setCheckedMail] = useState(false);

    const [modalVisible, setModalVisible] = useState(false);

    const [htmlreplace, setHtmlReplace] = useState();

    const [patient, setPatient] = useState('');

    const { settings } = useContext(GlobalContext);
    // Access EMR-related settings from the context
    let emrSetttings = settings?.EMR;
    /**
     * Memoizes the extraction of AI report-related details from `templateDetails`.
     *
     * This hook ensures that the `extractOtherDetails` function is only called
     * when `templateDetails` changes, preventing unnecessary recalculations on each render.
     */
    const { showAIReportGenerateBtn, buttonCaption, alertMessage } = useMemo(() => {
        return extractOtherDetails(templateDetails);
    }, [templateDetails]);

    let editorRef = useRef(null);

    /**
     * The function `searchGuestDetails` loads guest information based on the provided `opNo`.
     */
    const searchGuestDetails = () => {
        loadGuestInformation(opNo);
    };

    /**
     * The function "loadGuestInformation" loads guest information using the "loadGuestDetails" function
     * and sets the guest details using the response result.
     */
    const loadGuestInformation = async () => {
        setLoading(true);
        if (opNo !== null) {
            const response = await Opreg.getOpregDetails(opNo);
            // Reset all fields and html
            setInitial({});
            setHtml();
            setTemplateDetails();
            setMode();
            setCurrentReportId(null);
            form.resetFields();
            setGuestDetails(response.result);
            setLoading(false);
        } else {
            message.error('Enter valid opNo');
            setLoading(false);
        }
    };

    /**
     * Function to print report
     */
    const printReport = () => {
        if (window.tinymce && window.tinymce.activeEditor) {
            window.tinymce.activeEditor.execCommand('mcePrint');
        }
    };

    /**
     *
     */
    const handleCheckboxChange = (e) => {
        const isChecked = e.target.checked;
        setChecked(isChecked);
    };

    // Toggle the 'checked' state for the "Print" checkbox
    const print = () => {
        setChecked(!checked); // Flips the current checked state of 'checked'
    };

    // Toggle the 'checkedmail' state for the "Send as Mail" checkbox
    const checkemail = () => {
        setCheckedMail(!checkedmail); // Flips the current checked state of 'checkedmail'
    };
    /**
     * Function to save and edit medical reports
     * @param {*} param0
     * @returns
     */
    const saveMedicalReports = async ({ edit }) => {
        // Select the edit html as HTMLFormElement
        const Form = editorRef.current.editor.getDoc().querySelector('#myForm');

        let jsonDataFormat = {};
        // Loop through form fields and get their IDs and values as objects
        const fieldsData = {};

        if (Form) {
            // Get form data using FormData API
            const formData = new FormData(Form);

            formData.forEach(function (value, key) {
                const field = editorRef.current.editor.getDoc().querySelector(`[name="${key}"]`);

                if (field && field.id) {
                    fieldsData[[field.id]] = value;
                }
            });

            jsonDataFormat['data'] = fieldsData;

            // Do whatever you want with the JSON data
            setBtnloading(true);

            // Enable the hidden elements
            editorRef.current.editor.contentStyles = `.replaceable-content{
                 display:inline-block;
             }`;
        }

        const values = form.getFieldsValue();
        const body = {
            ...values,
            id: currentReportId,
            opno: opNo,
            //report add to htmltext
            // report: html,
            report: htmlreplace,
            html_form_data: jsonDataFormat,
            doctorptr: values.doctor,
            report_name: values.template,
        };

        return await MedicalReport.saveMedicalreport(body).then((result) => {
            if (result.success) {
                message.success(result.message);

                // if (checked) {
                //     printReport();
                // }
                if (checked == true || checkedmail == true) {
                    // printReport();
                    setModalVisible(true);
                }

                setCurrentReportId(result.id);
                // fillreport loading false
                setBtnloading(false);
                viewReportList();
            } else {
                message.error(result.message);
            }
            // This is done to change the mode after saving the medical report
            // If the status was saved as sign off mode is just view or edit is enabled
            if (values.report_status === 'S') {
                setMode('view');
            } else if (values.report_status === 'D') {
                setMode('edit');
            } else {
                // If the status is either, currentReportId is saved null, to prevent updation of teh record
                setCurrentReportId(null);
            }
        });
    };

    /**
     * Function to be feeded for reference select
     * to default the referrer for all
     * cases the one which has the lowest refr_code
     *
     * @param {*} options
     * @returns
     */
    function identifyDefaultReferrer(options = []) {
        let initial = 1;

        let matching = {};

        //To load lab report as default doctor
        if (options) {
            options.forEach(async (record) => {
                if (record['refr_code'] === guestDetails.ReferrerPointer) {
                    matching = record;
                }
            });
        }
        if (matching) {
            return matching;
        }
    }

    /**
     *  Load default Template
     * @param {*} options
     * @returns
     */

    const identifyDefaultTemplate = (options) => {
        let matching = null;

        if (options && Array.isArray(options)) {
            options.forEach((record) => {
                if (record && record.tmpl_code === templateCode) {
                    /**Set the matching template */
                    matching = record;
                }
            });
        }
        setOptions(options);
        if (matching) {
            setTemplateDetails(matching);
            /**  Set the template description*/
            setTemplate(matching.tmpl_desc);
            return matching;
        }

        return null; // Return null if no matching template found
    };

    /**
     *Function to Cancel
     * @returns
     */
    const handleCancel = () => {
        // await loadGuestInformation();
        setHtml(null);
        setMode();
        setCurrentReportId(null);
        identifyDefaultTemplate(null);
        setTemplateDetails({});
        setInitial({ report_status: 'D' });
        setChecked(false);
        setCheckedMail(false);
        setTimeout(() => {
            form.resetFields();
        }, 0);
    };

    /**
     * Function to clear
     * @returns
     */
    const handleClear = () => {
        // setOpNo(null);
        setGuestDetails('');
        setHtml(null);
        setTemplateDetails({});
        identifyDefaultTemplate(null);
        setTemplateLoading(false);
        form.resetFields();
        setInitial({ report_status: 'D' });
    };

    /**
     * @param {*} value
     * viewReportList
     *
     */

    async function viewReportList() {
        // Initially it is taken from guestDetails state but in that case Report list is not comming,
        // So opno is changes as taking from the opNo state, which is set during the input is typed itself
        const result = await ModalityGroups.getMedicalReport(opNo);

        setMedicalReport(result.report);
    }

    /**
     * Function to get report. This triggered when fill Report button is clicked
     * The report with the given template is loaded and shown.
     * The template is editable and then can saved or printed
     */
    async function getReport() {
        setTemplateLoading(true);
        setBtnloading(true);
        setCurrentReportId(null);
        setMode();

        if (!isShowVisitInfo) {
            return handleGenerateReport();
        }

        if (opNo) {
            /**If there is templateCode coming from attribute for setting manage ai report or set selectedTemplatedata */
            let templateData = templateDetails && templateDetails.tmpl_code ? templateDetails.tmpl_code : templateCode ? templateCode : null;
            if (templateData) {
                setTemplateLoading(true);
                let formbody = {
                    template_code: templateData,
                    referenceId: referenceId,
                };

                const res = await Opreg.getReport(formbody, opNo);
                setHtmlReplace(res.html);

                setHtml(res.html);

                setTemplateLoading(false);
                setBtnloading(false);
            } else {
                message.error('Please Select a Template');
                setTemplateLoading(false);
                setBtnloading(false);
            }
        } else {
            message.error('Please enter a valid OP Number');
            setTemplateLoading(false);
            setBtnloading(false);
        }
    }

    /**
     * @param {*} value
     * handleGenerateReport
     *
     */
    async function handleGenerateReport() {
        // Check if the templateDetails object is present and has a valid tmpl_code
        if (templateDetails?.tmpl_code || templateCode) {
            setTemplateLoading(true);

            setBtnloading(true);

            // Create the form body to pass to the API for generating the report
            let formbody = {
                template_code: templateDetails?.tmpl_code ? templateDetails.tmpl_code : templateCode, // Template code to identify which template to use
                referenceId: referenceId, // Reference ID associated with the report
                aiReport: true, // Flag to indicate that an AI report should be generated
            };

            // Call the API to get the report
            const result = await Opreg.getReport(formbody, opNo);

            if (result.success) {
                // Set the generated HTML content in the editor
                if (generateAiReportModal) {
                    // setReportModalVisible(true);
                    setHtml(result.html);
                    setTemplateLoading(false);
                    setBtnloading(false);
                    setHtmlReplace(result.html);
                }
                setHtml(result.html);
                setTemplateLoading(false);
            } else {
                // Show an error message with the result message
                message.error(result.message);
                setTemplateLoading(false); //loading false
                setBtnloading(false);
                // Ensure that the HTML content is not rendered or cleared
                setHtml(null);
            }

            // Set the loading state to false to indicate that the template generation is complete
            setTemplateLoading(false);
            setBtnloading(false);
        } else {
            // Display an error message if the template is not valid or not selected
            message.error('Please select a valid template');
            setTemplateLoading(false);
            setBtnloading(false);
        }
    }
    /**
     * Cancel Report
     * @param {*} values
     */
    async function cancelReport(record, values) {
        const result = await MedicalReport.cancelMedicalreport(record.id, { remarks: values.remarks });
        if (result.success) {
            message.success(result.message);
            setCurrentReportId(null);

            setHtml();
            // setInitial({})
            // await loadGuestInformation()
            // form.resetFields()
            setCancelReportModalVisible(false);
            viewReportList();
        } else {
            message.error(result.message);
        }
    }

    /**
     * Function to update editor
     */
    const handleUpdate = (value, editor, ...props) => {
        const editorContent = editor.getContent();
        // Update  state with only the editor body content
        setHtml(editorContent);
        // Replace the template
        let originalTemplate = templateDetails.tmpl_template;
        // Replace the entire <body> tag content with the new content from the editor
        const fullHtml = originalTemplate.replace(/<body[^>]*>.*<\/body>/s, `<body style="background-color: white;">${editorContent}</body>`);
        setHtmlReplace(fullHtml);
        setHtml(editorContent);
    };

    useEffect(() => {
        if (opNo) {
            viewReportList();
        }
    }, [opNo]);

    useEffect(() => {
        if (html) {
            setActiveTabKey('2');
        } else {
            setActiveTabKey('1');
        }
    }, [html]);

    // Function to handle the visibility of the modal based on checkbox states
    function handleModalVisibility() {
        // If the editor reference exists, get the current content from the editor and pass it along with the editor instance to handleUpdate()
        if (editorRef.current) {
            const editor = editorRef.current.editor;
            const value = editor.getContent();
            handleUpdate(value, editor);
        }
        // Check if either the "Print" checkbox (checked) or "Send as Mail" checkbox (checkedmail) is checked
        if (checked === true || checkedmail === true) {
            setModalVisible(true); // Show the modal if any of the checkboxes are checked
        } else {
            setModalVisible(false); // Hide the modal if neither checkbox is checked
        }
    }
    return (
        <>
            {loading ? (
                <Skeleton />
            ) : (
                <div className="medical-report">
                    <div className="card-wrapper">
                        {isShowVisitInfo && (
                            <VisitInfo
                                /**manage ai report modal */
                                generateAiReportModal={generateAiReportModal}
                                guestData={guestData}
                                enableBillnumber="true"
                                reference_number={reference_number}
                                onReceiveGuestDetails={(record) => {
                                    setHtml(null);
                                    handleClear();
                                    identifyDefaultTemplate(null);
                                    setPatient(record);
                                    setChecked(false); // Unchecks a checkbox
                                    setCheckedMail(false); // Also unchecks another checkbox,
                                }}
                                startLoading={() => setLoading(true)}
                                mode="opreg"
                                readOnlyMode={mode === 'view'}
                                isEditMode={mode === 'edit'}
                                disableCheckBox={disableCheckBox}
                                callback={async (values, bills) => {
                                    setReferenceId(values);
                                    setHtml();
                                    setCurrentReportId(null);
                                    let record = bills?.find((ele) => ele.opb_id === values);

                                    if (record) setOpNo(record.opb_opno);
                                }}
                            />
                        )}

                        <Card className="card card-shadow">
                            <Form
                                {...formItemLayout}
                                key={JSON.stringify(initial)}
                                initialValues={{
                                    ...initial,
                                }}
                                form={form}
                                onFinish={getReport}
                            >
                                <Form.Item label={t('TEMPLATE')} name="template">
                                    <ReferenceSelect
                                        config={{
                                            queries: [
                                                {
                                                    field: 'tmpl_groupptr',
                                                    value: 'PVMRTND',
                                                },
                                                {
                                                    field: 'tmpl_active',
                                                    value: 'Y',
                                                },
                                            ],
                                        }}
                                        /**If it is for managing ai report set default as ai reprot */
                                        // defaultValueCondition={(generateAiReportModal || !isShowVisitInfo) && identifyDefaultTemplate}
                                        defaultValueCondition={identifyDefaultTemplate}
                                        field="tmpl_code"
                                        label="tmpl_desc"
                                        mode="nura-base"
                                        disabled={currentReportId || generateAiReportModal || !isShowVisitInfo ? true : false}
                                        model={TemplateMasters}
                                        onChange={(e, options) => setTemplateDetails(options.record)}
                                        placeholder={t('Select Template')}
                                    />
                                </Form.Item>

                                <Form.Item label={t('REPORT STATUS')} name="report_status">
                                    <Select disabled={mode === 'view' || aiReportEditDisable} >
                                        <Option key="D">{t('Draft')}</Option>
                                        <Option key="S">{t('Sign Off')}</Option>
                                    </Select>
                                </Form.Item>

                                <Form.Item
                                    name="remarks"
                                    label={t('Remarks')}
                                    rules={[
                                        {
                                            required: false,
                                            message: t('Please Enter Remarks'),
                                        },
                                        {
                                            max: 50,
                                            message: t('Length cannot exceed 50 characters!'),
                                        },
                                    ]}
                                >
                                    <TextArea placeholder={t('Enter Remarks')} disabled={mode === 'view' || aiReportEditDisable}></TextArea>
                                </Form.Item>
                                {!generateAiReportModal && (
                                    <Form.Item>
                                        <Row gutter={20}>
                                            <Col xs={12} md={12} xl={12}>
                                                <Button
                                                    loading={btnloading}
                                                        disabled={aiReportEditDisable || currentReportId ? true : false} //  disable if aiReportEditDisable is true
                                                    type="primary"
                                                    htmlType="submit"
                                                    className="submit-button"
                                                    style={{ width: '100%' }}
                                                >
                                                    {t(showAIReportGenerateBtn ? buttonCaption : 'Fill Report')}
                                                </Button>
                                            </Col>
                                            <Col xs={12} md={12} xl={12}>
                                                <Button onClick={handleCancel} style={{ width: '100%' }} disabled={aiReportEditDisable ? true : false}>
                                                    {t('Cancel')}
                                                </Button>
                                            </Col>
                                        </Row>
                                    </Form.Item>
                                )}
                            </Form>

                            {!isShowVisitInfo && <CustomAlert type="info" message={alertMessage} />}

                            {isShowVisitInfo && showAIReportGenerateBtn && (
                                <AiNotes
                                    generateAiReportModal={generateAiReportModal}
                                    templateDetails={templateDetails}
                                    referenceId={referenceId}
                                    opNo={opNo}
                                    setTemplateLoading={setTemplateLoading}
                                    setHtml={setHtml}
                                ></AiNotes>
                            )}
                        </Card>
                    </div>

                    <Card>
                        <Tabs
                            activeKey={activeTabKey}
                            onChange={(key) => {
                                setActiveTabKey(key);

                                if (key === '1') {
                                    // Call only when tab 1 is selected
                                    viewReportList();
                                }
                            }}
                        >
                            <Tabs.TabPane key="1" tab="Report List">
                                <ReportList
                                    callback={(values, mode) => {
                                        if (document.activeElement) {
                                            document.activeElement.blur();
                                        }
                                        setMode(mode);
                                        setInitial(values);
                                        form.setFieldsValue(values);
                                        setCurrentReportId(values.id);
                                        setHtml(values.report);
                                        setHtmlReplace(values.report);
                                        // if there is template then set templatedetails
                                        //// Check if report_name exists in the values object
                                        if (values.report_name) {
                                            // Filter the options array to find elements matching the report_name
                                            let filter = options.filter((ele) => ele.tmpl_code === values.report_name);
                                            // Set the first matching item (if any) as the selected template details
                                            setTemplateDetails(filter[0]);
                                        }
                                    }}
                                    cancelReport={(values, record) => cancelReport(record, values)}
                                    medicalReport={medicalReport}
                                    cancelReportModalVisible={cancelReportModalVisible}
                                    setCancelReportModalVisible={setCancelReportModalVisible}
                                    template={template}
                                    opNo={opNo}
                                    form={form}
                                    aiReportEditDisable={aiReportEditDisable}
                                />
                            </Tabs.TabPane>
                            <Tabs.TabPane key="2" tab="Report">
                                {templateLoading ? (
                                    <Skeleton />
                                ) : html ? (
                                    <>
                                        <div className="left-section">
                                            <Checkbox checked={checked} onClick={print}>
                                                {t('Print')}
                                            </Checkbox>

                                            {/* This checkbox is managed by settings  and attribute*/}
                                            {emrSetttings?.enableEmailSend && props.enableEmailSendCheckBox ? (
                                                <>
                                                    <Checkbox checked={checkedmail} onClick={checkemail}>
                                                        {t('Send as Mail')}
                                                    </Checkbox>
                                                </>
                                            ) : null}

                                            <Button
                                                className="button-section"
                                                type="primary"
                                                disabled={mode === 'view' && !(checked || checkedmail)}
                                                onClick={mode === 'view' ? () => handleModalVisibility() : () => saveMedicalReports({ edit: true })}
                                            >
                                                {mode === 'view' ? t('Continue') : t('Save')}
                                            </Button>
                                        </div>
                                        <ReportEditor value={html} onChange={handleUpdate} mode={mode} editorRef={editorRef} />
                                    </>
                                ) : (
                                    <Empty />
                                )}
                                <Modal
                                    width={'100%'}
                                    destroyOnClose={true}
                                    footer={null}
                                    title={t('PRIVIEW REPORT')}
                                    visible={modalVisible}
                                    okText="Okay"
                                    onOk={() => {
                                        // setReportList(false);
                                    }}
                                    onCancel={() => {
                                        // setReportList(false);
                                        setModalVisible(false);
                                    }}
                                >
                                    <PrintPreview
                                        templatecode={templateDetails.tmpl_code}
                                        // templateOtherDetails={templateDetails.tmpl_otherdetails}
                                        templateDetails={templateDetails}
                                        html={htmlreplace}
                                        refereneceId={referenceId}
                                        isPdfPreviewMode={true}
                                        groupPointer={groupPointer}
                                        props={props}
                                        sendMail={checkedmail}
                                        checkPrint={checked}
                                        config={{
                                            recipient: {
                                                email: patient.email,
                                            },
                                        }}
                                        isEmailEditable={props.isEmailEditable}
                                    ></PrintPreview>
                                </Modal>
                            </Tabs.TabPane>
                        </Tabs>
                    </Card>
                </div>
            )}
        </>
    );
}
